import { z } from "zod";
import { BrandIdSchema, type BrandId } from "./brands";
import mobileAppsContract from "./generated/mobile-apps-contract";

export const MOBILE_APP_GET_PATH = "/mobile-app/get";

export const MOBILE_APP_PLATFORMS = ["android", "ios"] as const;
export type MobileAppPlatform = (typeof MOBILE_APP_PLATFORMS)[number];
export type MobileAppStoreUrls = Partial<Record<MobileAppPlatform, string>>;

const MobileAppConfigSchema = z.object({
  stores: z.object({ android: z.url().optional(), ios: z.url().optional() }),
  websiteDownload: z.boolean().default(true),
  appStoreTrackingUrl: z.url().optional(),
  loginHandoff: z.boolean().default(false),
});

export const MOBILE_APP_CATALOG = z
  .record(
    z.string(),
    z.object({
      source: z.enum(["mobile-app", "external"]),
      platforms: z.array(z.enum(MOBILE_APP_PLATFORMS)).min(1),
      alpha: z
        .object({
          stores: z.object({
            android: z.url().optional(),
            ios: z.url().optional(),
          }),
        })
        .optional(),
      brands: z.record(
        z.string().regex(/^[a-z0-9]+-[a-z]{2}$/),
        MobileAppConfigSchema.partial(),
      ),
    }),
  )
  .parse(mobileAppsContract.apps);

for (const [name, app] of Object.entries(MOBILE_APP_CATALOG)) {
  if (
    !Object.keys(app.brands).length ||
    Object.keys(app.brands).some((brand) => !brand.startsWith(`${name}-`))
  ) {
    throw new Error(`Invalid mobile brands for ${name}`);
  }
  if (new Set(app.platforms).size !== app.platforms.length)
    throw new Error(`Duplicate platforms for ${name}`);
  for (const entry of [...Object.values(app.brands), app.alpha ?? {}]) {
    if (
      Object.keys(entry.stores ?? {}).some(
        (platform) => !app.platforms.includes(platform as MobileAppPlatform),
      )
    ) {
      throw new Error(`Store platform is not supported by ${name}`);
    }
  }
}

const mobileApps = z
  .partialRecord(BrandIdSchema, MobileAppConfigSchema)
  .parse(
    Object.fromEntries(
      Object.values(MOBILE_APP_CATALOG).flatMap((app) =>
        Object.entries(app.brands).filter(
          ([id, brand]) => brand.stores && BrandIdSchema.safeParse(id).success,
        ),
      ),
    ),
  );

export const MOBILE_APP_BRAND_IDS = Object.keys(mobileApps) as BrandId[];
export const MOBILE_APP_DOWNLOAD_BRAND_IDS = MOBILE_APP_BRAND_IDS.filter(
  (brand) => mobileApps[brand]?.websiteDownload,
);

export const getMobileAppConfig = (brandId: BrandId) => mobileApps[brandId];

export const supportsMobileLoginHandoff = (brandId: BrandId): boolean =>
  mobileApps[brandId]?.loginHandoff === true;

export const getMobileAppStoreUrls = (brandId: BrandId): MobileAppStoreUrls =>
  mobileApps[brandId]?.stores ?? {};

export const getMobileAppStoreUrl = (
  brandId: BrandId,
  platform: MobileAppPlatform,
): string | null => getMobileAppStoreUrls(brandId)[platform] ?? null;
